import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { api } from '../services/api';
import { trackEvent } from '../services/analytics';
import type { TierContextValue, UserProfileResponse } from '../types/tier';

const TierContext = createContext<TierContextValue>({
  tier: null,
  monetizationEnabled: false,
  loading: true,
  isReadOnly: false,
  trialDaysRemaining: null,
  trialEndsAt: null,
  refresh: async () => {},
});

export function TierProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<UserProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const trialTrackedRef = useRef(false);

  const fetchProfile = useCallback(async () => {
    try {
      const data = await api.getUserProfile();
      setProfile(data);
      if (!trialTrackedRef.current && data.trialDaysRemaining != null && data.trialDaysRemaining > 0) {
        trialTrackedRef.current = true;
        trackEvent('trial_started');
      }
    } catch {
      // If profile fetch fails, treat as monetization off
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const value: TierContextValue = {
    tier: profile?.tier ?? null,
    monetizationEnabled: profile?.monetizationEnabled ?? false,
    loading,
    isReadOnly: profile?.isReadOnly ?? false,
    trialDaysRemaining: profile?.trialDaysRemaining ?? null,
    trialEndsAt: profile?.trialEndsAt ?? null,
    refresh: fetchProfile,
  };

  return <TierContext.Provider value={value}>{children}</TierContext.Provider>;
}

export function useTier(): TierContextValue {
  return useContext(TierContext);
}
